import React, { useRef, useState, useEffect } from "react";
import { Menu } from "@headlessui/react";
import {
  ChevronDownIcon,
  MagnifyingGlassIcon,
} from "@heroicons/react/24/outline";
import { useTranslation } from "react-i18next";

const CustomDropdownCountryAddress = ({
  menuItems,
  textColor,
  title,
  onSelect,
  selected,
  font,
  add_addresses,
  required = false,
  showError = false,
  errorMessage = "This field is required",
}) => {
  const { i18n } = useTranslation();
  const buttonRef = useRef(null);
  const searchInputRef = useRef(null);
  const [buttonWidth, setButtonWidth] = useState(0);
  const [selectedItem, setSelectedItem] = useState("");
  const [searchQuery, setSearchQuery] = useState("");
  const [hasError, setHasError] = useState(false);

  const sortedLocations = [...menuItems].sort((a, b) =>
    a.name.localeCompare(b.name, i18n.language === "ar" ? "ar" : "en", {
      sensitivity: "base",
    })
  );

  const filteredItems = sortedLocations.filter((item) =>
    item.name.toLowerCase().includes(searchQuery.toLowerCase())
  );

  useEffect(() => {
    if (buttonRef.current) {
      setButtonWidth(buttonRef.current.offsetWidth);
    }
  }, []);

  const { t } = useTranslation();

  const [direction, setDirection] = useState(
    localStorage.getItem("language") === "ar" ? "rtl" : "ltr"
  );

  useEffect(() => {
    const handleStorageChange = () => {
      setDirection(localStorage.getItem("language") === "ar" ? "rtl" : "ltr");
    };

    window.addEventListener("storage", handleStorageChange);
    return () => window.removeEventListener("storage", handleStorageChange);
  }, []);

  useEffect(() => {
    const language = localStorage.getItem("language");
    setDirection(language === "ar" ? "rtl" : "ltr");
  }, [localStorage.getItem("language")]);

  // Check if field is required and has no selection
  useEffect(() => {
    if (required && showError) {
      setHasError(!selected);
    }
  }, [required, selected, showError]);

  const handleMenuClose = () => {
    setSearchQuery("");
  };

  const handleMenuOpen = () => {
    setTimeout(() => {
      if (searchInputRef.current) {
        searchInputRef.current.focus();
      }
    }, 100);
  };

  const handleSelection = (item) => {
    setSelectedItem(item.name);
    setHasError(false); // Clear error when user selects an item
    onSelect(item);
  };

  return (
    <Menu as="div" className="relative inline-block w-full" dir={direction}>
      <div>
        <h4
          className={`${
            add_addresses
              ? "block mb-2 text-xs lg:text-sm font-semibold text-gray-700"
              : "block mb-2 text-sm font-semibold text-gray-700"
          }`}
        >
          {title}
          {required && <span className="ml-1 text-red-500">*</span>}
        </h4>
        <Menu.Button
          ref={buttonRef}
          className={`flex items-center ${
            font ? "" : "text-xs sm:text-lg"
          } justify-between rounded-md bg-white border w-full ${
            hasError ? "border-red-500" : "border-gray-300"
          } ${
            add_addresses ? "px-2 py-1 lg:px-5 lg:py-3" : "px-2 py-3 md:px-5"
          } font-medium text-black placeholder:text-sm ${
            hasError ? "focus:ring-red-500 focus:border-red-500" : ""
          }`}
        >
          <span
            className={`${
              add_addresses
                ? "lg:text-base text-black text-sm py-[2px] lg:py-0"
                : ""
            } ${!selected ? "text-gray-500" : "text-black"}`}
          >
            {menuItems.find((item) => item.id === selected)?.name ||
              t("calculateShippingSection.selectCountry")}
          </span>
          <ChevronDownIcon className="w-4 h-4 text-black lg:h-6 lg:w-6" />
        </Menu.Button>

        {/* Error Message */}
        {hasError && (
          <p className="mt-1 text-sm text-red-500">{errorMessage}</p>
        )}
      </div>

      <Menu.Items className="absolute z-40 w-full mt-1 overflow-y-auto bg-white border border-gray-300 rounded-md shadow-lg end-0 focus:outline-none max-h-52">
        {/* Search Input */}
        <div className="sticky top-0 p-2 bg-white border-b border-gray-200">
          <div className="relative">
            <MagnifyingGlassIcon className="absolute w-4 h-4 text-gray-400 transform -translate-y-1/2 left-3 top-1/2" />
            <input
              ref={searchInputRef}
              type="text"
              placeholder={direction === "rtl" ? "البحث..." : "Search..."}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full py-2 pl-10 pr-3 text-base text-black bg-white border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:border-transparent"
              onClick={(e) => e.stopPropagation()}
            />
          </div>
        </div>

        {filteredItems.map((item) => (
          <Menu.Item key={item.id}>
            {({ active }) => (
              <button
                onClick={() => handleSelection(item)}
                className={`block px-4 py-2 text-xs sm:text-sm text-start w-full flex items-center gap-2 ${
                  active ? "bg-gray-100 text-gray-900" : "text-gray-700"
                }`}
              >
                <img src={item.flag} alt={item.name} className="w-6 h-4 " />
                {item.name}
              </button>
            )}
          </Menu.Item>
        ))}
      </Menu.Items>
    </Menu>
  );
};

export default CustomDropdownCountryAddress;
